import React, { useState, useEffect } from "react";
import client from "../../api/client";
import { Layout } from "../../components/Layout/Layout";
import {
  ALL_ROUTES,
  ROLE_ROUTES,
  ROUTE_LABELS,
  ORDER_FIELD_LABELS,
  ROLE_FIELD_PERMISSIONS,
} from "../../utils/roleAccess";
import "./index.css";

interface StaffMember {
  id: number; username: string; name: string; role: string;
  canEdit: string[]; canView: string[]; routes: string[]; extraRoutes: string[];
  /** id сотрудника в Snippex: запросы из Snippex приходят с ним. */
  snippexUserId: string | null;
  /** Зарплата продажника: фикса в месяц (KZT) и процент с дохода. Страница
   *  «Команда» — только для admin, бэк больше никому их не отдаёт. */
  salaryFixed: number;
  salaryPercent: string | number;
}

const roles = ["admin", "sales", "editor", "headeditor", "marketing", "log", "operations"];

// Что роль даёт сама по себе — такие галочки показываем отмеченными и
// заблокированными: персональные права только ДОБАВЛЯЮТ, отобрать ролевое
// через них нельзя (см. utils/roleAccess.ts).
const roleGivesRoute = (role: string, route: string) =>
  (ROLE_ROUTES[role] || []).includes(route);
const roleGivesField = (role: string, field: string) =>
  (ROLE_FIELD_PERMISSIONS[role]?.canEdit || []).includes(field);

// Поля зарплаты показываем продажникам и всем, у кого она уже задана
// (продавать может и сотрудник с другой ролью).
const hasSalary = (role: string | undefined, fixed: unknown, percent: unknown) =>
  role === "sales" || Number(fixed) > 0 || Number(percent) > 0;

const toggle = (list: string[], value: string) =>
  list.includes(value) ? list.filter(v => v !== value) : [...list, value];

const Team: React.FC = () => {
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<StaffMember>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("sales");
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const { data } = await client.get("/staff"); setMembers(data); }
    catch {} finally { setLoading(false); }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser || !newPw || !newName) return;
    setCreating(true); setMsg(null);
    try {
      await client.post("/staff", { username: newUser, password: newPw, name: newName, role: newRole, canEdit: [], canView: [], routes: [], extraRoutes: [] });
      setMsg({ ok: true, text: `${newName} создан` });
      setNewUser(""); setNewPw(""); setNewName(""); setShowCreate(false);
      load();
    } catch (err: any) {
      setMsg({ ok: false, text: err.response?.data?.error || "Ошибка" });
    } finally { setCreating(false); }
  };

  const startEdit = (m: StaffMember) => {
    setEditId(m.id);
    setEditData({
      role: m.role,
      name: m.name,
      extraRoutes: m.extraRoutes || [],
      canEdit: m.canEdit || [],
      snippexUserId: m.snippexUserId || "",
      salaryFixed: m.salaryFixed ?? 0,
      salaryPercent: Number(m.salaryPercent ?? 0),
    });
  };

  const saveEdit = async () => {
    if (!editId) return;
    // Пустое поле — снять привязку (null), а не записать пустую строку:
    // у колонки уникальный индекс, две пустые строки бы конфликтовали.
    await client.patch(`/staff/${editId}`, {
      ...editData,
      snippexUserId: (editData.snippexUserId || "").trim() || null,
    });
    setEditId(null); load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Удалить?")) return;
    await client.delete(`/staff/${id}`); load();
  };


  return (
    <Layout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Команда</h2>
        <button className="btn-sm" onClick={() => setShowCreate(!showCreate)}>{showCreate ? "Отмена" : "+ Добавить"}</button>
      </div>

      {msg && <div className={`msg ${msg.ok ? "msg--ok" : "msg--err"}`}>{msg.text}</div>}

      {showCreate && (
        <form onSubmit={handleCreate} style={{ background: "#fff", padding: 16, border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 16, maxWidth: 360, display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="field"><label>Username</label><input value={newUser} onChange={e => setNewUser(e.target.value)} required /></div>
          <div className="field"><label>Пароль</label><input value={newPw} onChange={e => setNewPw(e.target.value)} required /></div>
          <div className="field"><label>Имя</label><input value={newName} onChange={e => setNewName(e.target.value)} required /></div>
          <div className="field"><label>Роль</label>
            <select value={newRole} onChange={e => setNewRole(e.target.value)}>
              {roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button type="submit" className="btn" disabled={creating}>{creating ? "..." : "Создать"}</button>
        </form>
      )}

      {loading ? <div style={{ color: "var(--text-3)" }}>Загрузка...</div> : (
        <div>
          {members.map(m => (
            <div key={m.id} className="staff-card">
              {editId === m.id ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div className="field" style={{ flex: 1 }}><label>Имя</label><input value={editData.name || ""} onChange={e => setEditData({ ...editData, name: e.target.value })} /></div>
                    <div className="field" style={{ flex: 1 }}><label>Роль</label>
                      <select value={editData.role || ""} onChange={e => setEditData({ ...editData, role: e.target.value })}>
                        {roles.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>

                  {hasSalary(editData.role, m.salaryFixed, m.salaryPercent) && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <div className="field" style={{ flex: 1 }}>
                        <label>Фикса, ₸ в месяц</label>
                        <input
                          type="number"
                          min={0}
                          step={1000}
                          value={editData.salaryFixed ?? 0}
                          onChange={e => setEditData({ ...editData, salaryFixed: Number(e.target.value) })}
                        />
                      </div>
                      <div className="field" style={{ flex: 1 }}>
                        <label>Процент с дохода, %</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={editData.salaryPercent ?? 0}
                          onChange={e => setEditData({ ...editData, salaryPercent: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  <div className="field">
                    <label>ID в Polygram</label>
                    <input
                      value={editData.snippexUserId || ""}
                      onChange={e => setEditData({ ...editData, snippexUserId: e.target.value })}
                      placeholder="Не привязан"
                    />
                    <div className="perm-hint">
                      Polygram → Настройки → Сотрудники → «Скопировать ID» под именем. Нужен, чтобы действия из Polygram (создание аккаунта, постоплата и др.) записывались на этого сотрудника.
                    </div>
                  </div>

                  <div className="field">
                    <label>Страницы сверх роли</label>
                    <div className="perm-grid">
                      {ALL_ROUTES.map(route => {
                        const byRole = roleGivesRoute(editData.role || "", route);
                        return (
                          <label key={route} className={`perm-item${byRole ? " perm-item--locked" : ""}`}>
                            <input
                              type="checkbox"
                              disabled={byRole}
                              checked={byRole || (editData.extraRoutes || []).includes(route)}
                              onChange={() => setEditData({ ...editData, extraRoutes: toggle(editData.extraRoutes || [], route) })}
                            />
                            {ROUTE_LABELS[route] || route}
                          </label>
                        );
                      })}
                    </div>
                    <div className="perm-hint">Отмеченные серым даёт роль — снять нельзя.</div>
                  </div>

                  <div className="field">
                    <label>Поля заказа сверх роли</label>
                    <div className="perm-grid">
                      {ORDER_FIELD_LABELS.map(({ key, label }) => {
                        const byRole = roleGivesField(editData.role || "", key);
                        return (
                          <label key={key} className={`perm-item${byRole ? " perm-item--locked" : ""}`}>
                            <input
                              type="checkbox"
                              disabled={byRole}
                              checked={byRole || (editData.canEdit || []).includes(key)}
                              onChange={() => setEditData({ ...editData, canEdit: toggle(editData.canEdit || [], key) })}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn-sm" onClick={saveEdit}>Сохранить</button>
                    <button className="btn-sm" onClick={() => setEditId(null)}>Отмена</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div className="staff-name">{m.name}</div>
                    <div className="staff-meta">
                      {m.username} &middot; {m.role}
                      {m.snippexUserId ? <> &middot; Polygram привязан</> : null}
                    </div>
                    {hasSalary(m.role, m.salaryFixed, m.salaryPercent) && (
                      <div className="staff-meta">
                        Фикса {Number(m.salaryFixed || 0).toLocaleString("ru-RU")} ₸ &middot;{" "}
                        {Number(m.salaryPercent || 0).toLocaleString("ru-RU")}% с дохода
                      </div>
                    )}
                    {(m.extraRoutes || []).length > 0 && (
                      <div className="staff-meta">
                        + {m.extraRoutes.map(r => ROUTE_LABELS[r] || r).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="staff-actions">
                    <button className="btn-sm" onClick={() => startEdit(m)}>Изменить</button>
                    <button className="btn-sm btn-sm--danger" onClick={() => handleDelete(m.id)}>Удалить</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
};

export default Team;
